/**
 * NavBar
 *
 * Renders the admin navigation menu, filtering items based on the current
 * user's role. Route-access rules are derived from the centralized
 * ROLE_ROUTE_ACCESS matrix in src/permissions/index.ts so that
 * nav visibility stays in sync with route guards automatically.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Item } from "./styles";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import isMobile from "is-mobile";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useEffect, useMemo, useState } from "react";
import { usePermission } from "../../../hooks/use-permission";

const navItems = [
  {
    path: "/admin",
    label: "Dashboard",
    icon: <HomeOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
  {
    path: "/following-profiles",
    label: "Perfis em acompanhamento",
    icon: <RemoveRedEyeOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
  {
    path: "/clients",
    label: "Perfis Ativos",
    icon: <ListOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
  {
    path: "/sellers",
    label: "Usuários",
    icon: <GroupOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
  {
    path: "/cases",
    label: "Cases de sucesso",
    icon: <TrendingUpOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
  {
    path: "/email",
    label: "Conectar Email",
    icon: <EmailOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
  {
    path: "/backlinks-dashboard",
    label: "Dashboard de Backlinks",
    icon: <LinkOutlinedIcon sx={{ fontSize: "20px" }} />,
  },
];

export const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const { canAccess, hasRole } = usePermission();

  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filteredNavItems = useMemo(
    () => navItems.filter((item) => {
      if (!canAccess(item.path)) return false;
      if (item.path === "/backlinks-dashboard" && hasRole("ADMIN")) return false;
      return true;
    }),
    [canAccess, hasRole],
  );

  useEffect(() => {
    const currentNavItem = filteredNavItems.find((item) =>
      location.pathname.startsWith(item.path),
    );
    if (currentNavItem) {
      setActivePath(currentNavItem.path);
    } else {
      setActivePath(location.pathname);
    }
  }, [location.pathname, filteredNavItems]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  if (isMobile()) {
    return (
      <>
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            marginRight: "8vw",
            zIndex: 1101,
          }}
        >
          <MenuIcon sx={{ fontSize: "30px" }} />
        </IconButton>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{ zIndex: 1300 }}
        >
          <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={() => setDrawerOpen(false)}
            onKeyDown={() => setDrawerOpen(false)}
          >
            <List>
              {filteredNavItems.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={activePath === item.path}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Container>
      {filteredNavItems.map((item) => (
        <Item
          key={item.path}
          $isActive={location.pathname.startsWith(item.path)}
          onClick={() => handleNavigate(item.path)}
        >
          {item.icon}
          <span>{item.label}</span>
        </Item>
      ))}
    </Container>
  );
};
